import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, KeyRound, Eye, EyeOff } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingKeys, setSavingKeys] = useState(false);
  const [gateway, setGateway] = useState("");
  const [limit, setLimit] = useState(0);
  const [keys, setKeys] = useState({});
  const [show, setShow] = useState({});

  const load = () => api.get("/admin/settings").then(({ data }) => {
    setSettings(data); setGateway(data.active_gateway); setLimit(data.default_ticket_limit);
    const blank = {}; Object.keys(data.keys_status || {}).forEach((k) => { blank[k] = ""; }); setKeys(blank);
  }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const saveGateway = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", { active_gateway: gateway, default_ticket_limit: Number(limit) }); toast.success("Settings saved."); load(); }
    catch (e) { toast.error(getErr(e)); } finally { setSaving(false); }
  };

  const saveKeys = async () => {
    const payload = {}; Object.entries(keys).forEach(([k, v]) => { if (v && v.trim()) payload[k] = v.trim(); });
    if (Object.keys(payload).length === 0) { toast.error("Enter at least one key to update."); return; }
    setSavingKeys(true);
    try { await api.put("/admin/settings", { keys: payload }); toast.success("API keys saved to vault."); load(); }
    catch (e) { toast.error(getErr(e)); } finally { setSavingKeys(false); }
  };

  if (loading || !settings) return <Spinner />;

  return (
    <div data-testid="admin-settings">
      <h2 className="font-display uppercase text-2xl text-white">Settings</h2>
      <div className="mt-4 grid lg:grid-cols-2 gap-5">
        <NeonCard variant="flow" innerClassName="p-6">
          <p className="font-display uppercase text-xl text-white">Payment Gateway</p>
          <label className="mt-4 block text-xs uppercase tracking-widest text-[var(--text-muted)]">Active gateway</label>
          <select value={gateway} onChange={(e) => setGateway(e.target.value)} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white" data-testid="settings-gateway">
            {(settings.available_gateways || []).map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <label className="mt-4 block text-xs uppercase tracking-widest text-[var(--text-muted)]">Default ticket limit per user</label>
          <input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white" data-testid="settings-ticket-limit" />
          <NeonButton variant="pink" className="mt-4" onClick={saveGateway} loading={saving} data-testid="save-settings"><Save className="h-4 w-4" /> Save Settings</NeonButton>
        </NeonCard>

        <NeonCard variant="cyan" innerClassName="p-6">
          <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[var(--neon-cyan)]" /><p className="font-display uppercase text-xl text-white">API Key Vault</p></div>
          <p className="text-xs text-[var(--text-muted)]">Securely store gateway / service keys on the backend. Leave blank to keep the current value. Saved keys are never shown again.</p>
          <div className="mt-3 space-y-2" data-testid="api-key-vault">
            {Object.entries(settings.keys_status || {}).map(([k, isSet]) => (
              <div key={k}>
                <div className="flex items-center justify-between"><label className="text-xs text-[var(--text-muted)]">{k}</label><Pill color={isSet ? "green" : "grey"}>{isSet ? "Set" : "Empty"}</Pill></div>
                <div className="mt-1 flex gap-2">
                  <input type={show[k] ? "text" : "password"} value={keys[k] || ""} onChange={(e) => setKeys((s) => ({ ...s, [k]: e.target.value }))} placeholder={isSet ? "●●●●●● (unchanged)" : "Enter key"} className="flex-1 bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid={`key-input-${k}`} />
                  <button onClick={() => setShow((s) => ({ ...s, [k]: !s[k] }))} className="px-3 rounded-lg border border-white/10 text-[var(--text-muted)]">{show[k] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </div>
              </div>
            ))}
          </div>
          <NeonButton variant="outlineCyan" className="mt-4" onClick={saveKeys} loading={savingKeys} data-testid="save-keys"><Save className="h-4 w-4" /> Save Keys</NeonButton>
        </NeonCard>
      </div>
    </div>
  );
}
